import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { map, catchError } from 'rxjs/operators';
import { Observable, of, lastValueFrom } from 'rxjs';
import * as Entities from '@shared/entities';
import * as Models from '@shared/models';
import { LoggerService } from '../services/logger.service';
import { ConfigService } from '../services/config.service';
import { UsageAnalyticsService } from '../services/usage-analytics.service';

@Injectable({
  providedIn: 'root',
})
export class RetailerProgramsApi {
  serviceUri = '';

  constructor(
    private http: HttpClient,
    private log: LoggerService,
    private config: ConfigService,
    private usageAnalytics: UsageAnalyticsService
  ) {
    this.config.Config.subscribe((config) => {
      if (config?.apiUri) {
        this.serviceUri = `${config.apiUri}/retailerPrograms/`;
      }
    });
  }

  createRetailerProgram(
    retailerPrograms: Entities.RetailerProgram[]
  ): Observable<Entities.RetailerProgram | undefined | string> {
    const requestModel: Models.IRetailerProgramRequestModel = {
      retailerPrograms: retailerPrograms,
    };
    return this.http
      .post<Entities.RetailerProgram>(this.serviceUri, JSON.stringify(requestModel), {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        map((response: Entities.RetailerProgram) => {
          this.usageAnalytics.trackAction('RetailerProgramAdded', {
            props: {
              RetailerId: retailerPrograms
                .map((program) => program.retailerId)
                .filter(Boolean)
                .join(','),
              ProgramIds: retailerPrograms
                .map((program) => program.programId)
                .filter(Boolean)
                .join(','),
              RetailerProgramId: response?.retailerProgramId === undefined ? '' : String(response.retailerProgramId),
            },
          });
          return response;
        }),
        catchError(this.handleError<Entities.RetailerProgram | undefined>('CreateRetailerProgram', undefined))
      );
  }

  updateRetailerProgram(
    retailerProgram: Entities.RetailerProgram
  ): Observable<Entities.RetailerProgram | undefined | string> {
    return this.http
      .put<Entities.RetailerProgram>(
        this.serviceUri + retailerProgram.retailerProgramId,
        JSON.stringify(retailerProgram),
        { headers: { 'Content-Type': 'application/json' } }
      )
      .pipe(
        map((response: Entities.RetailerProgram) => {
          this.usageAnalytics.trackAction('RetailerProgramSettingsUpdated', {
            props: {
              RetailerId: String(retailerProgram.retailerId ?? ''),
              ProgramId: String(retailerProgram.programId ?? ''),
              RetailerProgramId: String(retailerProgram.retailerProgramId ?? ''),
            },
          });
          return response;
        }),
        catchError(this.handleError<Entities.RetailerProgram | undefined>('PutRetailerProgram', undefined))
      );
  }

  async listProviderServiceKeys(
    retailerProgramId: number
  ): Promise<Models.Result<Models.ListResponse<Entities.ProviderServiceKey>>> {
    try {
      return await lastValueFrom(
        this.http.get<Models.Result<Models.ListResponse<Entities.ProviderServiceKey>>>(
          this.serviceUri + retailerProgramId + '/providerServiceKeys'
        )
      );
    } catch (error: unknown) {
      return this.createFailureResult<Models.ListResponse<Entities.ProviderServiceKey>>(
        error,
        'Unable to load retailer program provider service keys.',
        { records: [] } as Models.ListResponse<Entities.ProviderServiceKey>
      );
    }
  }

  async addProviderServiceKey(retailerProgramId: number, providerServiceKeyId: number): Promise<Models.Result<void>> {
    try {
      const result = await lastValueFrom(
        this.http.post<Models.Result<void>>(
          this.serviceUri + retailerProgramId + '/providerServiceKeys/' + providerServiceKeyId,
          {}
        )
      );

      if (result.successful) {
        this.log.displaySuccess(
          'Retailer program provider service key added',
          'RetailerProgramsApi->addProviderServiceKey'
        );
        this.usageAnalytics.trackAction('RetailerProgramProviderServiceKeyAdded', {
          props: {
            RetailerProgramId: String(retailerProgramId),
            ProviderServiceKeyId: String(providerServiceKeyId),
          },
        });
      } else {
        this.log.displayError(result.message, 'RetailerProgramsApi->addProviderServiceKey');
      }

      return result;
    } catch (error: unknown) {
      const failureResult = this.createFailureResult<void>(
        error,
        'Unable to add retailer program provider service key.',
        undefined as void
      );
      this.log.displayError(failureResult.message, 'RetailerProgramsApi->addProviderServiceKey');
      return failureResult;
    }
  }

  async removeProviderServiceKey(
    retailerProgramId: number,
    providerServiceKeyId: number
  ): Promise<Models.Result<void>> {
    try {
      const result = await lastValueFrom(
        this.http.delete<Models.Result<void>>(
          this.serviceUri + retailerProgramId + '/providerServiceKeys/' + providerServiceKeyId
        )
      );

      if (result.successful) {
        this.log.displaySuccess(
          'Retailer program provider service key removed',
          'RetailerProgramsApi->removeProviderServiceKey'
        );
        this.usageAnalytics.trackAction('RetailerProgramProviderServiceKeyRemoved', {
          props: {
            RetailerProgramId: String(retailerProgramId),
            ProviderServiceKeyId: String(providerServiceKeyId),
          },
        });
      } else {
        this.log.displayError(result.message, 'RetailerProgramsApi->removeProviderServiceKey');
      }

      return result;
    } catch (error: unknown) {
      const failureResult = this.createFailureResult<void>(
        error,
        'Unable to remove retailer program provider service key.',
        undefined as void
      );
      this.log.displayError(failureResult.message, 'RetailerProgramsApi->removeProviderServiceKey');
      return failureResult;
    }
  }

  private handleError<T>(_operation = 'operation', result?: T): (error: unknown) => Observable<T> {
    return (error: unknown): Observable<T> => {
      console.error(error);
      return of(result as T);
    };
  }

  private createFailureResult<T>(error: unknown, fallbackMessage: string, fallbackData: T): Models.Result<T> {
    if (error instanceof HttpErrorResponse) {
      const body = error.error as Partial<Models.Result<T>> | null | undefined;
      const message =
        typeof body?.message === 'string' && body.message.length > 0 ? body.message : error.message || fallbackMessage;
      const data = (body?.data as T | undefined) ?? fallbackData;
      return new Models.Result<T>(false, message, data, []);
    }

    if (error instanceof Error) {
      return new Models.Result<T>(false, error.message || fallbackMessage, fallbackData, []);
    }

    return new Models.Result<T>(false, fallbackMessage, fallbackData, []);
  }
}
